import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { submitLeadMagnet } from "@/lib/lead-magnet.functions";
import type { LeadMagnet } from "@/lib/lead-magnets";

function storageKey(key: string) {
  return `kcn-lm-${key}`;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

type Errors = Partial<Record<"firstName" | "email" | "phone" | "smsConsent" | "form", string>>;

export function LeadMagnetBlock({ magnet }: { magnet: LeadMagnet }) {
  const submit = useServerFn(submitLeadMagnet);
  const [unlocked, setUnlocked] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [building, setBuilding] = useState(false);
  const [blob, setBlob] = useState<Blob | null>(null);

  useEffect(() => {
    try {
      if (window.localStorage.getItem(storageKey(magnet.key))) setUnlocked(true);
    } catch {
      /* storage unavailable */
    }
  }, [magnet.key]);

  // Build the PDF ahead of the click so the download keeps user activation.
  useEffect(() => {
    if (!unlocked || blob) return;
    let alive = true;
    setBuilding(true);
    magnet
      .buildPdf()
      .then((b) => alive && setBlob(b))
      .catch(() => alive && setErrors({ form: "The playbook didn't load. Please refresh and try again." }))
      .finally(() => alive && setBuilding(false));
    return () => {
      alive = false;
    };
  }, [unlocked, blob, magnet]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next: Errors = {};
    if (!firstName.trim()) next.firstName = "First name is required.";
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) next.email = "Enter a valid email.";
    if (phone.replace(/\D/g, "").length < 7) next.phone = "Phone is required.";
    if (!consent) next.smsConsent = "Please check the box to agree to receive text messages before continuing.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setBusy(true);
    try {
      let sessionId: string | undefined;
      try {
        sessionId = window.sessionStorage.getItem("kcn_analytics_sid") ?? undefined;
      } catch {
        /* ignore */
      }
      await submit({
        data: {
          key: magnet.key,
          firstName,
          email,
          phone,
          smsConsent: true,
          path: window.location.pathname,
          sessionId,
        },
      });
      try {
        window.localStorage.setItem(storageKey(magnet.key), new Date().toISOString());
      } catch {
        /* ignore */
      }
      setUnlocked(true);
    } catch (err: any) {
      setErrors({ form: err?.message || "Something went wrong. Please try again." });
    } finally {
      setBusy(false);
    }
  }

  const input =
    "w-full bg-transparent border border-cream/30 px-4 py-3 text-cream placeholder:text-cream/45 focus:outline-none focus:border-cream";
  const label = "block text-xs uppercase tracking-[0.18em] text-cream/70 mb-1.5";
  const errCls = "mt-1.5 text-sm text-cream font-medium";

  return (
    <aside
      id="lead-magnet"
      aria-labelledby={`lm-${magnet.key}`}
      className="mt-12 scroll-mt-28 border-2 border-primary border-t-8 bg-background p-6 md:p-10 shadow-lg"
    >
      <div className="text-xs font-bold uppercase tracking-[0.24em] text-cream mb-2">{magnet.eyebrow}</div>
      <h2 id={`lm-${magnet.key}`} className="font-display text-3xl md:text-4xl text-cream leading-tight">
        {magnet.title}
      </h2>
      <p className="mt-3 text-lg text-cream/85">{magnet.oneLiner}</p>

      {unlocked ? (
        <div className="mt-6">
          <button
            type="button"
            disabled={!blob}
            onClick={() => blob && downloadBlob(blob, magnet.fileName)}
            className="inline-block bg-primary ring-1 ring-cream/40 px-6 py-3 text-xs uppercase tracking-[0.2em] text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {building || !blob ? "Preparing the playbook..." : "Download the playbook"}
          </button>
          {errors.form ? <p className={errCls}>{errors.form}</p> : null}
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate className="mt-6 grid gap-4 md:grid-cols-3">
          <div>
            <label htmlFor="lm-first" className={label}>First name</label>
            <input id="lm-first" className={input} autoComplete="given-name" maxLength={80} value={firstName} onChange={(e) => setFirstName(e.target.value)} aria-invalid={!!errors.firstName} />
            {errors.firstName ? <p className={errCls}>{errors.firstName}</p> : null}
          </div>
          <div>
            <label htmlFor="lm-email" className={label}>Email</label>
            <input id="lm-email" type="email" className={input} autoComplete="email" maxLength={255} value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={!!errors.email} />
            {errors.email ? <p className={errCls}>{errors.email}</p> : null}
          </div>
          <div>
            <label htmlFor="lm-phone" className={label}>Phone</label>
            <input id="lm-phone" type="tel" className={input} autoComplete="tel" maxLength={30} value={phone} onChange={(e) => setPhone(e.target.value)} aria-invalid={!!errors.phone} />
            {errors.phone ? <p className={errCls}>{errors.phone}</p> : null}
          </div>

          <div className="md:col-span-3">
            <label className="flex items-start gap-3 text-sm leading-relaxed text-cream/80">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                aria-invalid={!!errors.smsConsent}
                className="mt-1 h-4 w-4 shrink-0 accent-primary"
              />
              <span>
                I agree to receive SMS messages from Kristan Cole Network, a DBA of Cole Investments Inc., including
                appointment confirmations, scheduling information, reminders, responses to inquiries, and related
                follow-up communications. Message frequency varies. Message and data rates may apply. Reply STOP to
                opt out. Reply HELP for assistance. See our{" "}
                <Link to="/terms" className="text-cream underline">Terms &amp; Conditions</Link> and{" "}
                <Link to="/privacy" className="text-cream underline">Privacy Policy</Link>.
              </span>
            </label>
            {errors.smsConsent ? <p role="alert" className={errCls}>{errors.smsConsent}</p> : null}
          </div>

          <div className="md:col-span-3">
            <button
              type="submit"
              disabled={busy}
              className="inline-block bg-primary ring-1 ring-cream/40 px-6 py-3 text-xs uppercase tracking-[0.2em] text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {busy ? "Sending..." : "Get the playbook"}
            </button>
            {errors.form ? <p role="alert" className={errCls}>{errors.form}</p> : null}
          </div>
        </form>
      )}
    </aside>
  );
}
